/**
 * VisualBuilder — the drag-and-drop workflow builder tab.
 *
 * Layout (desktop ≥ 1024 px):
 *
 *   +----------+----------------------+-----------+
 *   | palette  |  canvas (steps)      |  triggers |
 *   | (left)   |  (center, cream)     |  (right)  |
 *   +----------+----------------------+-----------+
 *   |              preview pane (sticky)          |
 *   +---------------------------------------------+
 *
 * The component is a "second view" over the same {@link WorkflowDraft}
 * the structured composer edits; the parent ({@link WorkflowEditorView})
 * is the single source of truth.
 *
 * DnD wiring: one {@link DndContext} scopes the palette + drop zones. Drop
 * routing is done here in `onDragEnd` based on the droppable id prefix
 * (cond-zone / act-zone / trig-zone) — keeping per-zone components
 * presentation-only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import type {
  ActionSpec,
  LoanFieldDescriptor,
  NotificationRecipient,
  StepCategory,
  StepSpec,
  TriggerSpec,
} from '@mana/shared';
import { api } from '../../../lib/api.js';
import { FieldPalette, parseFieldDragId } from './FieldPalette.js';
import { StepCanvas } from './StepCanvas.js';
import { TriggerPanel, parseTriggerZoneId } from './TriggerPanel.js';
import { PreviewPane } from './PreviewPane.js';
import { parseConditionZoneId } from './ConditionDropZone.js';
import { parseActionZoneId } from './ActionsDropZone.js';
import type { ActionCatalogRecipient } from './ActionRow.js';
import {
  lookupField,
  makeConditionRowFromField,
  makeEmptyActionRow,
  makeEmptyGroup,
  stepSpecsToVisualSteps,
  triggerSpecsToVisualTriggers,
  visualStepsToStepSpecs,
  visualTriggersToTriggerSpecs,
  type VisualStep,
  type VisualTrigger,
} from './serialize.js';

// ---------------------------------------------------------------------------
// Catalog wire shapes (mirror /actions/catalog + /fields/catalog).
// ---------------------------------------------------------------------------

interface FieldsCatalogResponse {
  fields: LoanFieldDescriptor[];
}

interface ActionCatalogEntry {
  kind: 'email' | 'whatsapp' | 'sms' | 'internal_task';
  label: string;
  description: string;
  recipients?: Array<{
    id: NotificationRecipient;
    label: string;
    description: string;
    requiresContactSetup: boolean;
  }>;
  assignees?: Array<{ id: string; label: string }>;
}

interface ActionsCatalogResponse {
  actions: ActionCatalogEntry[];
  stepKeyRegex: string;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface VisualBuilderProps {
  /** Current steps in canonical {@link StepSpec} shape (lifted from the
   *  parent's {@link WorkflowDraft}). */
  steps: readonly StepSpec[];
  /** Current triggers in canonical shape. */
  triggers: readonly TriggerSpec[];
  /** True when the workflow is locked (ADMIN_PUBLISHED viewed by non-admin). */
  readOnly: boolean;
  /** Bumps the dirty flag on the parent. */
  onChange: (next: { steps: StepSpec[]; triggers: TriggerSpec[] }) => void;
}

export function VisualBuilder({
  steps,
  triggers,
  readOnly,
  onChange,
}: VisualBuilderProps) {
  // ---- catalogs --------------------------------------------------------------
  const fieldsQuery = useQuery<FieldsCatalogResponse>({
    queryKey: ['workflows', 'fields-catalog'],
    queryFn: () => api<FieldsCatalogResponse>(`/api/v1/workflows/fields/catalog`),
    staleTime: 5 * 60 * 1000,
  });
  const actionsQuery = useQuery<ActionsCatalogResponse>({
    queryKey: ['workflows', 'actions-catalog'],
    queryFn: () => api<ActionsCatalogResponse>(`/api/v1/workflows/actions/catalog`),
    staleTime: 5 * 60 * 1000,
  });

  const emailRecipients: ActionCatalogRecipient[] = useMemo(
    () =>
      actionsQuery.data?.actions.find((a) => a.kind === 'email')?.recipients ?? [],
    [actionsQuery.data],
  );
  const phoneRecipients: ActionCatalogRecipient[] = useMemo(() => {
    const a = actionsQuery.data?.actions;
    if (!a) return [];
    // whatsapp and sms share the same role set; pick whichever is present.
    return (
      a.find((x) => x.kind === 'whatsapp')?.recipients ??
      a.find((x) => x.kind === 'sms')?.recipients ??
      []
    );
  }, [actionsQuery.data]);

  // ---- local visual state -----------------------------------------------------
  //
  // We mirror the canonical step list into the "visual" shape on the first
  // hydration; subsequent canonical-side changes (e.g. the user saved and
  // the server returned a new key set) reset the visual state via the
  // hydration ref below.
  const [visualSteps, setVisualSteps] = useState<VisualStep[]>([]);
  const [visualTriggers, setVisualTriggers] = useState<VisualTrigger[]>([]);
  const [extrasByStepKey, setExtrasByStepKey] = useState<Record<string, ActionSpec[]>>(
    {},
  );
  const [selectedStepKey, setSelectedStepKey] = useState<string | null>(null);

  // Hydration ref — track the structural signature of the incoming props so
  // we only reset when the canonical list shape changes from outside (i.e.
  // not in response to our own emits).
  const lastSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const sig = JSON.stringify({
      keys: steps.map((s) => s.key),
      triggerTypes: triggers.map((t) => t.type),
    });
    if (lastSignatureRef.current === sig) return;
    lastSignatureRef.current = sig;
    const { visualSteps: vs, extrasByStepKey: ex } = stepSpecsToVisualSteps(steps);
    setVisualSteps(vs);
    setExtrasByStepKey(ex);
    setVisualTriggers(triggerSpecsToVisualTriggers(triggers));
    if (!selectedStepKey && vs.length > 0) setSelectedStepKey(vs[0]?.key ?? null);
  }, [steps, triggers, selectedStepKey]);

  // Whenever the visual state changes we project it back into canonical
  // shape and bubble to the parent. We deliberately re-emit on every edit;
  // the parent already debounces dirty + Save.
  const emit = useCallback(
    (
      nextSteps: VisualStep[],
      nextTriggers: VisualTrigger[],
      nextExtras: Record<string, ActionSpec[]>,
    ) => {
      const canonicalSteps = visualStepsToStepSpecs(nextSteps, nextExtras);
      const canonicalTriggers = visualTriggersToTriggerSpecs(nextTriggers);
      onChange({ steps: canonicalSteps, triggers: canonicalTriggers });
    },
    [onChange],
  );

  const patchSteps = useCallback(
    (update: (cur: VisualStep[]) => VisualStep[]) => {
      setVisualSteps((cur) => {
        const next = update(cur);
        emit(next, visualTriggers, extrasByStepKey);
        return next;
      });
    },
    [emit, visualTriggers, extrasByStepKey],
  );

  const patchTriggers = useCallback(
    (update: (cur: VisualTrigger[]) => VisualTrigger[]) => {
      setVisualTriggers((cur) => {
        const next = update(cur);
        emit(visualSteps, next, extrasByStepKey);
        return next;
      });
    },
    [emit, visualSteps, extrasByStepKey],
  );

  // ---- handlers --------------------------------------------------------------

  const handlePatchStep = useCallback(
    (key: string, next: VisualStep) => {
      patchSteps((cur) => cur.map((s) => (s.key === key ? next : s)));
    },
    [patchSteps],
  );

  const handleRemoveStep = useCallback(
    (key: string) => {
      patchSteps((cur) => cur.filter((s) => s.key !== key));
      setExtrasByStepKey((cur) => {
        const next = { ...cur };
        delete next[key];
        return next;
      });
      if (selectedStepKey === key) {
        setSelectedStepKey((cur) => {
          const remaining = visualSteps.filter((s) => s.key !== key);
          return remaining[0]?.key ?? null;
        });
      }
    },
    [patchSteps, selectedStepKey, visualSteps],
  );

  const handleAddStep = useCallback(() => {
    patchSteps((cur) => {
      const nextIdx = cur.length + 1;
      const key = nextStepKey(cur, nextIdx);
      const fresh: VisualStep = {
        key,
        name: `Step ${nextIdx}`,
        category: 'NOTIFY' as StepCategory,
        cooldownHours: 0,
        isTerminal: false,
        groupLogic: 'or',
        conditionGroups: [makeEmptyGroup()],
        actionRows: [],
        unsupportedGuard: false,
      };
      setSelectedStepKey(key);
      return [...cur, fresh];
    });
  }, [patchSteps]);

  const handleAddTrigger = useCallback(
    (kind: TriggerSpec['type']) => {
      patchTriggers((cur) => [...cur, freshTrigger(kind)]);
    },
    [patchTriggers],
  );

  const handlePatchTrigger = useCallback(
    (id: string, next: VisualTrigger) => {
      patchTriggers((cur) => cur.map((t) => (t.id === id ? next : t)));
    },
    [patchTriggers],
  );

  const handleRemoveTrigger = useCallback(
    (id: string) => {
      patchTriggers((cur) => cur.filter((t) => t.id !== id));
    },
    [patchTriggers],
  );

  // Add a field as a condition row to a step. When `groupId` is given (a field
  // dropped on a specific group), the row lands in that group; otherwise it
  // goes to the step's last group (keyboard "+ Add to step" fallback).
  const handleAddFieldToStep = useCallback(
    (fieldId: string, stepKey: string, groupId?: string) => {
      const field = lookupField(fieldId);
      if (!field) return;
      patchSteps((cur) =>
        cur.map((s) => {
          if (s.key !== stepKey) return s;
          const groups = s.conditionGroups.length > 0 ? s.conditionGroups : [makeEmptyGroup()];
          const targetId = groupId ?? groups[groups.length - 1]!.id;
          return {
            ...s,
            conditionGroups: groups.map((g) =>
              g.id === targetId
                ? { ...g, rows: [...g.rows, makeConditionRowFromField(field)] }
                : g,
            ),
          };
        }),
      );
      setSelectedStepKey(stepKey);
    },
    [patchSteps],
  );

  const handleAddFieldToTrigger = useCallback(
    (fieldId: string) => {
      const field = lookupField(fieldId);
      if (!field) return;
      patchTriggers((cur) => {
        // Find the first condition trigger, or create one.
        const condIdx = cur.findIndex((t) => t.spec.type === 'condition');
        if (condIdx >= 0) {
          const target = cur[condIdx];
          if (!target) return cur;
          const updated: VisualTrigger = {
            ...target,
            predicateRows: [
              ...(target.predicateRows ?? []),
              makeConditionRowFromField(field),
            ],
            predicateLogic: target.predicateLogic ?? 'and',
          };
          return cur.map((t, i) => (i === condIdx ? updated : t));
        }
        const fresh = freshTrigger('condition');
        fresh.predicateRows = [makeConditionRowFromField(field)];
        fresh.predicateLogic = 'and';
        return [...cur, fresh];
      });
    },
    [patchTriggers],
  );

  // ---- DnD wiring ------------------------------------------------------------

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const overId = event.over?.id;
      const activeId = event.active.id;
      if (typeof overId !== 'string' || typeof activeId !== 'string') return;

      const fieldId = parseFieldDragId(activeId);
      if (!fieldId) return;
      const field = lookupField(fieldId);
      if (!field) return;

      const condZone = parseConditionZoneId(overId);
      if (condZone) {
        handleAddFieldToStep(fieldId, condZone.stepKey, condZone.groupId);
        return;
      }
      const actStepKey = parseActionZoneId(overId);
      if (actStepKey) {
        // Action zones don't accept fields (they accept action tokens), but
        // we silently no-op rather than throw to keep the UX forgiving.
        return;
      }
      const triggerId = parseTriggerZoneId(overId);
      if (triggerId) {
        patchTriggers((cur) =>
          cur.map((t) =>
            t.id === triggerId && t.spec.type === 'condition'
              ? {
                  ...t,
                  predicateRows: [
                    ...(t.predicateRows ?? []),
                    makeConditionRowFromField(field),
                  ],
                  predicateLogic: t.predicateLogic ?? 'and',
                }
              : t,
          ),
        );
      }
    },
    [handleAddFieldToStep, patchTriggers],
  );

  // ---- derived ---------------------------------------------------------------
  const selectedStep =
    visualSteps.find((s) => s.key === selectedStepKey) ?? visualSteps[0] ?? null;

  const stepOptions = useMemo(
    () => visualSteps.map((s) => ({ key: s.key, name: s.name })),
    [visualSteps],
  );

  // ---- render ----------------------------------------------------------------
  if (fieldsQuery.isLoading || actionsQuery.isLoading) {
    return (
      <div className="flex h-96 items-center justify-center text-sm text-slate-500">
        Loading builder…
      </div>
    );
  }
  if (fieldsQuery.isError || actionsQuery.isError) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        Couldn&apos;t load the builder catalogs. Switch to the Structured form tab
        to keep editing.
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      {/* Desktop-first 3-pane builder; on phones the panes keep their widths and
          the whole canvas pans horizontally instead of crushing to zero. */}
      <div className="overflow-x-auto">
      <div className="grid h-[calc(100vh-280px)] min-h-[640px] min-w-[880px] grid-cols-[260px_minmax(0,1fr)_320px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
        <div className="overflow-hidden">
          <FieldPalette
            fields={fieldsQuery.data?.fields ?? []}
            stepOptions={stepOptions}
            onAddFieldToStep={handleAddFieldToStep}
            onAddFieldToTrigger={handleAddFieldToTrigger}
          />
        </div>
        <div className="overflow-y-auto">
          <StepCanvas
            steps={visualSteps}
            readOnly={readOnly}
            emailRecipients={emailRecipients}
            phoneRecipients={phoneRecipients}
            onPatchStep={(key, next) => {
              handlePatchStep(key, next);
              setSelectedStepKey(key);
            }}
            onRemoveStep={handleRemoveStep}
            onAddStep={handleAddStep}
          />
        </div>
        <div className="overflow-hidden">
          <TriggerPanel
            triggers={visualTriggers}
            steps={stepOptions}
            readOnly={readOnly}
            onAdd={handleAddTrigger}
            onPatch={handlePatchTrigger}
            onRemove={handleRemoveTrigger}
          />
        </div>
      </div>
      </div>
      <PreviewPane step={selectedStep} />
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STEP_KEY_VALID = /^[A-Z0-9_-]{2,32}$/;

function nextStepKey(existing: readonly VisualStep[], requested: number): string {
  const taken = new Set(existing.map((s) => s.key));
  let counter = requested;
  // STEP_1, STEP_2 ... or STEP_<n> if the user keeps adding/removing.
  for (;;) {
    const candidate = `STEP_${counter}`;
    if (!taken.has(candidate) && STEP_KEY_VALID.test(candidate)) return candidate;
    counter += 1;
  }
}

function freshTrigger(kind: TriggerSpec['type']): VisualTrigger {
  const id = `vtrig_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  if (kind === 'time') {
    return {
      id,
      spec: {
        type: 'time',
        mode: 'relative_offset',
        anchorField: 'fundingDate',
        offsetDays: 30,
        timezone: 'America/Chicago',
      },
    };
  }
  if (kind === 'event') {
    return { id, spec: { type: 'event', event: 'loan.status_changed' } };
  }
  return {
    id,
    spec: {
      type: 'condition',
      predicate: { op: 'exists', path: 'loan.id' },
    },
    predicateRows: [],
    predicateLogic: 'and',
    predicateUnsupported: false,
  };
}
